import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Edit } from 'lucide-react';

const CATEGORIES = ['outdoors', 'crafts', 'cooking', 'painting', 'social', 'exercise', 'learning', 'entertainment', 'relaxation'];
const TIME_OPTIONS = ['5 mins', '15 mins', '30 mins', '1 hr', '1-2 hrs', '2+ hrs'];

const EditIdeaPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ title: '', description: '', category: '', time_needed: '' });

  useEffect(() => {
    fetchIdea();
  }, [id]);

  const fetchIdea = async () => {
    try {
      const response = await axios.get(`/ideas/${id}`);
      const idea = response.data;
      setFormData({ title: idea.title, description: idea.description, category: idea.category, time_needed: idea.time_needed });
    } catch (error) {
      toast.error('Failed to fetch idea');
      navigate('/my-ideas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.put(`/ideas/${id}`, formData);
      toast.success('Idea updated!');
      navigate('/my-ideas');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update idea');
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutralLight">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutralLight">
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="sticky top-0 z-40 bg-white shadow-soft border-b border-gray-100"
      >
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-4">
          <motion.button
            whileHover={{ scale: 1.1, x: -5 }}
            whileTap={{ scale: 0.9 }}
            data-testid="back-to-my-ideas-btn"
            onClick={() => navigate('/my-ideas')}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors focus-ring"
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          <Edit className="w-5 h-5 text-blueFun" />
          <h1 className="text-2xl font-baloo font-bold text-neutralDark">Edit Idea</h1>
        </div>
      </motion.header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-card p-6"
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-semibold text-neutralDark">Title</Label>
              <Input
                id="title"
                data-testid="edit-title-input"
                placeholder="What's your idea?"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="h-12 rounded-xl border-2 focus-ring"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-semibold text-neutralDark">Description</Label>
              <Textarea
                id="description"
                data-testid="edit-description-input"
                placeholder="Share more details..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={6}
                className="rounded-xl border-2 resize-none focus-ring"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-neutralDark">Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger data-testid="edit-category-select" className="h-12 rounded-xl border-2 focus-ring">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-neutralDark">Time Needed</Label>
                <Select value={formData.time_needed} onValueChange={(value) => setFormData({ ...formData, time_needed: value })}>
                  <SelectTrigger data-testid="edit-time-select" className="h-12 rounded-xl border-2 focus-ring">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {TIME_OPTIONS.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={() => navigate('/my-ideas')}
                variant="outline"
                className="flex-1 h-12 rounded-full border-2"
              >
                Cancel
              </Button>
              <motion.div className="flex-1" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  data-testid="update-idea-btn"
                  type="submit"
                  className="w-full h-12 bg-blueFun hover:bg-blueFun/90 text-white font-bold rounded-full shadow-soft focus-ring"
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </motion.div>
            </div>
          </form>
        </motion.div>
      </main>
    </div>
  );
};

export default EditIdeaPage;
